import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Building2, Plus, MapPin, Users, Package, TrendingUp,
  Loader2, Check, ChevronRight, Store, DollarSign,
  BarChart3, ArrowRight, AlertTriangle, RefreshCw, Navigation
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { DashboardLayout } from "./Dashboard";
import { useAuth } from "../../App";
import apiClient from "../../lib/api";
import { toastApiError } from "../../lib/toastApiError";

const BusinessAccountPage = () => {
  const { t, i18n } = useTranslation("pharmacy");
  const numberLocale = i18n.language === "en" ? "en-US" : "fr-FR";
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [consolidatedStats, setConsolidatedStats] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  // Form state for adding new pharmacy
  const [formData, setFormData] = useState({
    entity_name: "",
    entity_type: "pharmacie",
    address: "",
    city: "",
    commune: "",
    phone: "",
    latitude: null,
    longitude: null
  });
  const [photo, setPhoto] = useState(null);
  const [identityDoc, setIdentityDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pharmaciesRes, statsRes] = await Promise.all([
        apiClient.get("/business/pharmacies"),
        apiClient.get("/business/stats")
      ]);
      setData(pharmaciesRes.data);
      setConsolidatedStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching business data:", error);
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("business.toastGeoUnsupported"));
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData({
          ...formData,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        toast.success(t("business.toastGeoSuccess"));
        setGettingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        let errorMsg = t("business.toastGeoError");
        if (error.code === 1) errorMsg = t("business.toastGeoDenied");
        if (error.code === 2) errorMsg = t("business.toastGeoUnavailable");
        if (error.code === 3) errorMsg = t("business.toastGeoTimeout");
        toast.error(errorMsg);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleSwitchPharmacy = async (pharmacyId) => {
    setSwitchingId(pharmacyId);
    try {
      const res = await apiClient.post("/business/pharmacies/switch", {
        pharmacy_id: pharmacyId
      });
      
      // Update local user state
      if (setUser && user) {
        setUser({
          ...user,
          pharmacy_id: pharmacyId,
          active_pharmacy_id: pharmacyId
        });
      }
      
      toast.success(res.data.message);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error switching pharmacy:", error);
      toastApiError(error);
    } finally {
      setSwitchingId(null);
    }
  };

  const handleAddPharmacy = async (e) => {
    e.preventDefault();
    
    if (!photo || !identityDoc) {
      toast.error(t("business.toastNeedDocs"));
      return;
    }
    
    setSubmitting(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append("entity_name", formData.entity_name);
      formDataObj.append("entity_type", formData.entity_type);
      formDataObj.append("address", formData.address);
      formDataObj.append("city", formData.city);
      formDataObj.append("commune", formData.commune || "");
      formDataObj.append("phone", formData.phone);
      if (formData.latitude) formDataObj.append("latitude", formData.latitude);
      if (formData.longitude) formDataObj.append("longitude", formData.longitude);
      formDataObj.append("photo", photo);
      formDataObj.append("identity_doc", identityDoc);
      
      await apiClient.post("/business/pharmacies/add", formDataObj, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(t("business.toastAdded"));
      setShowAddDialog(false);
      setFormData({
        entity_name: "",
        entity_type: "pharmacie",
        address: "",
        city: "",
        commune: "",
        phone: "",
        latitude: null,
        longitude: null
      });
      setPhoto(null);
      setIdentityDoc(null);
      fetchData();
    } catch (error) {
      console.error("Error adding pharmacy:", error);
      toastApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(numberLocale, {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0) + " CDF";
  };

  if (loading) {
    return (
      <DashboardLayout title={t("business.title")}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-12 w-12 animate-spin text-omu-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  const pharmacies = data?.pharmacies || [];
  const activePharmacyId = data?.active_pharmacy_id;
  const isBusinessAccount = data?.is_business_account || pharmacies.length > 1;

  return (
    <DashboardLayout title={t("business.title")}>
      <div className="space-y-6" data-testid="business-account-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-omu-blue-500" />
              <h1 className="text-2xl font-bold text-slate-900">
                {isBusinessAccount ? t("business.title") : t("business.myPharmacies")}
              </h1>
              {isBusinessAccount && (
                <Badge className="bg-purple-100 text-purple-700">{t("business.badge")}</Badge>
              )}
            </div>
            <p className="text-slate-500 mt-1">
              {t("business.subtitle")}
            </p>
          </div>
          
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-omu-blue-500 hover:bg-omu-blue-600 rounded-full"
            data-testid="add-pharmacy-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("business.addPharmacy")}
          </Button>
        </div>

        {/* Consolidated Stats (only for business accounts) */}
        {isBusinessAccount && consolidatedStats && (
          <Card className="border-0 shadow-soft bg-gradient-to-r from-omu-blue-500 to-blue-600 text-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                {t("business.consolidatedStats")}
              </CardTitle>
              <CardDescription className="text-blue-100">
                {t("business.consolidatedDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-blue-100 text-sm">{t("business.statPharmacies")}</p>
                  <p className="text-3xl font-bold">{consolidatedStats.total_pharmacies}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-blue-100 text-sm">{t("business.statInventory")}</p>
                  <p className="text-3xl font-bold">{consolidatedStats.total_inventory}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-blue-100 text-sm">{t("business.statSellers")}</p>
                  <p className="text-3xl font-bold">{consolidatedStats.total_sellers}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-blue-100 text-sm">{t("business.statRevenue")}</p>
                  <p className="text-2xl font-bold">{formatCurrency(consolidatedStats.total_revenue_month)}</p>
                </div>
              </div>
              
              {consolidatedStats.total_profit_month > 0 && (
                <div className="mt-4 pt-4 border-t border-white/20">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-100">{t("business.totalProfit")}</span>
                    <span className="text-2xl font-bold text-green-300">
                      {formatCurrency(consolidatedStats.total_profit_month)}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pharmacies List */}
        <div className="grid gap-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {t("business.yourPharmacies", { count: pharmacies.length })}
          </h2>
          
          {pharmacies.length === 0 ? (
            <Card className="border-0 shadow-soft">
              <CardContent className="py-12 text-center">
                <Store className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{t("business.noPharmacy")}</p>
                <Button
                  onClick={() => setShowAddDialog(true)}
                  className="mt-4 bg-omu-blue-500 hover:bg-omu-blue-600"
                >
                  {t("business.addFirst")}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {pharmacies.map((pharmacy) => (
                <Card
                  key={pharmacy.id}
                  className={`border-0 shadow-soft transition-all ${
                    pharmacy.id === activePharmacyId
                      ? "ring-2 ring-omu-blue-500 bg-omu-blue-50/30"
                      : "hover:shadow-md"
                  }`}
                  data-testid={`pharmacy-card-${pharmacy.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg text-slate-900">
                            {pharmacy.entity_name}
                          </h3>
                          {pharmacy.id === activePharmacyId && (
                            <Badge className="bg-omu-blue-100 text-omu-blue-700">
                              {t("business.active")}
                            </Badge>
                          )}
                          {pharmacy.status === "pending" && (
                            <Badge className="bg-amber-100 text-amber-700">
                              {t("status.pending")}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                          <MapPin className="h-3 w-3" />
                          {pharmacy.address}, {pharmacy.city}
                        </div>
                        
                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-3 mt-4">
                          <div className="text-center p-2 bg-slate-50 rounded-lg">
                            <Package className="h-4 w-4 mx-auto text-slate-400" />
                            <p className="text-lg font-semibold text-slate-900">
                              {pharmacy.stats?.inventory_count || 0}
                            </p>
                            <p className="text-xs text-slate-500">{t("business.products")}</p>
                          </div>
                          <div className="text-center p-2 bg-slate-50 rounded-lg">
                            <Users className="h-4 w-4 mx-auto text-slate-400" />
                            <p className="text-lg font-semibold text-slate-900">
                              {pharmacy.stats?.sellers_count || 0}
                            </p>
                            <p className="text-xs text-slate-500">{t("business.sellers")}</p>
                          </div>
                          <div className="text-center p-2 bg-slate-50 rounded-lg">
                            <TrendingUp className="h-4 w-4 mx-auto text-slate-400" />
                            <p className="text-lg font-semibold text-slate-900">
                              {pharmacy.stats?.today_sales || 0}
                            </p>
                            <p className="text-xs text-slate-500">{t("business.salesPerDay")}</p>
                          </div>
                        </div>
                        
                        {/* Subscription status */}
                        <div className="mt-3">
                          {pharmacy.stats?.has_subscription ? (
                            <Badge className="bg-green-100 text-green-700">
                              <Check className="h-3 w-3 mr-1" />
                              {t("status.subscribed")}
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-600">
                              {t("status.noSubscription")}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      {pharmacy.id !== activePharmacyId ? (
                        <Button
                          onClick={() => handleSwitchPharmacy(pharmacy.id)}
                          disabled={switchingId === pharmacy.id || pharmacy.status === "pending"}
                          className="flex-1 bg-omu-blue-500 hover:bg-omu-blue-600"
                          data-testid={`switch-to-${pharmacy.id}`}
                        >
                          {switchingId === pharmacy.id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-2" />
                          )}
                          {t("business.switchTo")}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => navigate("/dashboard")}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <ArrowRight className="h-4 w-4 mr-2" />
                          {t("business.goDashboard")}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Per-pharmacy breakdown (for business accounts) */}
        {isBusinessAccount && consolidatedStats?.pharmacies_stats?.length > 0 && (
          <Card className="border-0 shadow-soft">
            <CardHeader>
              <CardTitle>{t("business.performanceTitle")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {consolidatedStats.pharmacies_stats.map((stat, index) => (
                  <div
                    key={stat.pharmacy_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                        index === 0 ? "bg-yellow-500" :
                        index === 1 ? "bg-slate-400" :
                        index === 2 ? "bg-orange-400" : "bg-slate-300"
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{stat.name}</p>
                        <p className="text-sm text-slate-500">{stat.city}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-omu-blue-600">
                        {formatCurrency(stat.revenue_month)}
                      </p>
                      {stat.profit_month > 0 && (
                        <p className="text-sm text-green-600">
                          {t("business.profit", { amount: formatCurrency(stat.profit_month) })}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add Pharmacy Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-omu-blue-500" />
                {t("business.addDialogTitle")}
              </DialogTitle>
              <DialogDescription>
                {t("business.addDialogDesc")}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleAddPharmacy} className="space-y-4">
              <div>
                <Label htmlFor="entity_name">{t("business.entityName")}</Label>
                <Input
                  id="entity_name"
                  value={formData.entity_name}
                  onChange={(e) => setFormData({ ...formData, entity_name: e.target.value })}
                  placeholder={t("business.entityNamePlaceholder")}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="entity_type">{t("business.entityType")}</Label>
                <Select
                  value={formData.entity_type}
                  onValueChange={(value) => setFormData({ ...formData, entity_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pharmacie">{t("business.typePharmacy")}</SelectItem>
                    <SelectItem value="clinique">{t("business.typeClinic")}</SelectItem>
                    <SelectItem value="cabinet">{t("business.typeCabinet")}</SelectItem>
                    <SelectItem value="hopital">{t("business.typeHospital")}</SelectItem>
                    <SelectItem value="autre">{t("business.typeOther")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">{t("business.cityOptional")}</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder={t("business.cityPlaceholder")}
                  />
                </div>
                <div>
                  <Label htmlFor="commune">{t("business.commune")}</Label>
                  <Input
                    id="commune"
                    value={formData.commune}
                    onChange={(e) => setFormData({ ...formData, commune: e.target.value })}
                    placeholder={t("business.communePlaceholder")}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="address">{t("business.address")}</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder={t("business.addressPlaceholder")}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="phone">{t("business.phoneOptional")}</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+243 ..."
                />
              </div>
              
              {/* GPS Location */}
              <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2">
                    <Navigation className="h-4 w-4 text-omu-blue-500" />
                    {t("business.gpsLocation")}
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={detectLocation}
                    disabled={gettingLocation}
                    className="text-omu-blue-600 border-omu-blue-200 hover:bg-omu-blue-50"
                  >
                    {gettingLocation ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t("business.detecting")}
                      </>
                    ) : (
                      <>
                        <MapPin className="h-4 w-4 mr-2" />
                        {t("business.detectLocation")}
                      </>
                    )}
                  </Button>
                </div>
                {formData.latitude && formData.longitude ? (
                  <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
                    <Check className="h-4 w-4" />
                    <span>{t("business.locationDetected", { lat: formData.latitude.toFixed(6), lng: formData.longitude.toFixed(6) })}</span>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">
                    {t("business.gpsHint")}
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="photo">{t("business.photo")}</Label>
                <Input
                  id="photo"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="identity_doc">{t("business.identityDoc")}</Label>
                <Input
                  id="identity_doc"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setIdentityDoc(e.target.files?.[0] || null)}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  {t("business.identityDocHint")}
                </p>
              </div>
              
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    {t("business.validationNotice")}
                  </p>
                </div>
              </div>
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                >
                  {t("actions.cancel")}
                </Button>
                <Button
                  type="submit"
                  className="bg-omu-blue-500 hover:bg-omu-blue-600"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  {t("actions.add")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BusinessAccountPage;
